/**
 * Offline-first visit draft store (IndexedDB).
 *
 * Two object stores:
 *   - drafts:   keyed by lead_id -> { lead_id, form, uploaded_photo_ids, updated_at }
 *   - photos:   keyed by local_id -> { local_id, lead_id, blob, filename, mime, created_at }
 *
 * Photos are the binary blobs the user captured but hasn't yet uploaded to the server.
 * Once uploaded, we move their server file_id into drafts.uploaded_photo_ids and delete the photo row.
 */

const DB_NAME = "rana_offline";
const DB_VERSION = 1;
const STORE_DRAFTS = "drafts";
const STORE_PHOTOS = "photos";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        db.createObjectStore(STORE_DRAFTS, { keyPath: "lead_id" });
      }
      if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
        const s = db.createObjectStore(STORE_PHOTOS, { keyPath: "local_id" });
        s.createIndex("by_lead", "lead_id", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(storeNames, mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeNames, mode);
    const done = fn(t);
    t.oncomplete = () => resolve(done);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

function reqPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------- Drafts ----------
export async function saveDraft(lead_id, form, uploaded_photo_ids = []) {
  await tx([STORE_DRAFTS], "readwrite", (t) => {
    t.objectStore(STORE_DRAFTS).put({
      lead_id,
      form,
      uploaded_photo_ids,
      updated_at: new Date().toISOString(),
    });
  });
}

export async function loadDraft(lead_id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_DRAFTS], "readonly");
    const req = t.objectStore(STORE_DRAFTS).get(lead_id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteDraft(lead_id) {
  await tx([STORE_DRAFTS, STORE_PHOTOS], "readwrite", (t) => {
    t.objectStore(STORE_DRAFTS).delete(lead_id);
    const idx = t.objectStore(STORE_PHOTOS).index("by_lead");
    idx.openCursor(IDBKeyRange.only(lead_id)).onsuccess = (e) => {
      const c = e.target.result;
      if (c) { c.delete(); c.continue(); }
    };
  });
}

export async function listAllDrafts() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_DRAFTS], "readonly");
    const req = t.objectStore(STORE_DRAFTS).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

// ---------- Pending Photos ----------
export async function addPendingPhoto({ lead_id, blob, filename, mime, section = "property", tag = "other" }) {
  const local_id = `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  await tx([STORE_PHOTOS], "readwrite", (t) => {
    t.objectStore(STORE_PHOTOS).put({
      local_id, lead_id, blob, filename, mime, section, tag,
      created_at: new Date().toISOString(),
    });
  });
  return local_id;
}

export async function listPendingPhotos(lead_id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_PHOTOS], "readonly");
    const idx = t.objectStore(STORE_PHOTOS).index("by_lead");
    const out = [];
    idx.openCursor(IDBKeyRange.only(lead_id)).onsuccess = (e) => {
      const c = e.target.result;
      if (c) { out.push(c.value); c.continue(); }
      else resolve(out);
    };
    t.onerror = () => reject(t.error);
  });
}

export async function removePendingPhoto(local_id) {
  await tx([STORE_PHOTOS], "readwrite", (t) => {
    t.objectStore(STORE_PHOTOS).delete(local_id);
  });
}

export async function countAllPendingPhotos() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_PHOTOS], "readonly");
    const req = t.objectStore(STORE_PHOTOS).count();
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror = () => reject(req.error);
  });
}
